// 解决方案列表与详情
import { getPortalContentDetail, getPortalContents } from '@/api/portal'
import { findContentByRouteKey } from '@/components/home/utils/homeSectionItemLink'
import {
  buildSolutionMenuMap,
  isSolutionsSectionPath,
  normalizeHomeSolutions,
  normalizeSolutionDetail,
  normalizeSolutionItem
} from '@/components/modules/solutions/utils/solutionNormalize'

let solutionFetchSeq = 0
let inflightSolutionFetch = null
const detailFetchSeqMap = new Map()

function shouldLoadSolutions(path, rootMenu) {
  return isSolutionsSectionPath(path, rootMenu)
}

export default {
  namespaced: true,
  state: {
    items: [],
    loading: false,
    loaded: false,
    locale: '',
    scopedRootKey: '',
    detailMap: {},
    detailLoadingMap: {}
  },
  getters: {
    homeItems(state) {
      return state.items
    },
    allItems(state) {
      return state.items
    },
    getBySlug: (state) => (routeKey) => findContentByRouteKey(state.items, routeKey),
    getDetailById: (state) => (id) => {
      if (!id) return null
      const key = String(id)
      return Object.prototype.hasOwnProperty.call(state.detailMap, key)
        ? state.detailMap[key]
        : undefined
    },
    isDetailLoading: (state) => (id) => Boolean(state.detailLoadingMap[String(id)]),
    hasContent(state) {
      return state.items.length > 0
    }
  },
  mutations: {
    SET_ITEMS(state, { items, locale, scopedRootKey = '' }) {
      state.items = items || []
      state.loaded = true
      state.locale = locale || ''
      state.scopedRootKey = scopedRootKey || ''
    },
    CLEAR_DETAIL_CACHE(state) {
      state.detailMap = {}
      state.detailLoadingMap = {}
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    RESET(state) {
      state.items = []
      state.loaded = false
      state.locale = ''
      state.loading = false
      state.detailMap = {}
      state.detailLoadingMap = {}
    },
    SET_DETAIL(state, { id, detail }) {
      state.detailMap = {
        ...state.detailMap,
        [String(id)]: detail
      }
    },
    SET_DETAIL_LOADING(state, { id, loading }) {
      state.detailLoadingMap = {
        ...state.detailLoadingMap,
        [String(id)]: loading
      }
    }
  },
  actions: {
    async fetchSolutions({ commit, dispatch, state, rootState }, options) {
      const { force = false } = options ?? {}
      const locale = rootState.locale.current
      if (!locale) return

      const scopedRootKey = options?.rootMenu?.id != null ? String(options.rootMenu.id) : ''
      if (!force && state.loaded && state.locale === locale) {
        if (state.scopedRootKey !== scopedRootKey) {
          commit('SET_ITEMS', { items: state.items, locale, scopedRootKey })
        }
        return
      }

      if (force) {
        solutionFetchSeq += 1
        inflightSolutionFetch = null
      }

      if (inflightSolutionFetch?.locale === locale && !force) {
        return inflightSolutionFetch.promise
      }

      const seq = ++solutionFetchSeq
      const task = (async () => {
        commit('SET_LOADING', true)
        commit('CLEAR_DETAIL_CACHE')
        try {
          const params = { type: 'SOLUTION', locale }
          if (force) params._t = Date.now()
          const [contents, menus] = await Promise.all([
            getPortalContents(params),
            dispatch('nav/ensureRawMenus', null, { root: true })
          ])
          if (seq !== solutionFetchSeq) return
          const rootMenu = options?.rootMenu || null
          commit('SET_ITEMS', {
            items: normalizeHomeSolutions(contents, menus, rootMenu),
            locale,
            scopedRootKey
          })
        } catch (error) {
          if (seq !== solutionFetchSeq) return
          commit('SET_ITEMS', { items: [], locale, scopedRootKey })
          throw error
        } finally {
          if (seq === solutionFetchSeq) {
            commit('SET_LOADING', false)
          }
        }
      })()

      inflightSolutionFetch = { locale, promise: task }
      try {
        await task
      } finally {
        if (inflightSolutionFetch?.promise === task) {
          inflightSolutionFetch = null
        }
      }
    },
    reloadByPath({ dispatch }, payload) {
      const path = typeof payload === 'string' ? payload : payload?.path
      const force = typeof payload === 'object' && payload ? Boolean(payload.force) : false
      const rootMenu = typeof payload === 'object' && payload ? payload.rootMenu || null : null
      if (!shouldLoadSolutions(path, rootMenu)) return Promise.resolve()
      return dispatch('fetchSolutions', { force, rootMenu })
    },
    async resolveSolutionListItem({ dispatch, getters, rootState }, payload = {}) {
      const contentId = payload.contentId || payload.slug
      if (!contentId) return null

      const rootMenu = payload.rootMenu || null
      await dispatch('fetchSolutions', { rootMenu })

      const cached = getters.getBySlug(contentId)
      if (cached) return cached

      if (!/^\d+$/.test(String(contentId))) return null

      const locale = rootState.locale.current
      if (!locale) return null

      try {
        const data = await getPortalContentDetail(contentId, locale)
        if (!data || data.type !== 'SOLUTION') return null

        const menus = await dispatch('nav/ensureRawMenus', null, { root: true })
        return normalizeSolutionItem(
          data,
          buildSolutionMenuMap(menus, rootMenu),
          rootMenu
        )
      } catch {
        return null
      }
    },
    async fetchSolutionDetail({ commit, rootState, state }, id) {
      const locale = rootState.locale.current
      if (!locale || !id) return null

      const key = String(id)
      if (Object.prototype.hasOwnProperty.call(state.detailMap, key)) {
        return state.detailMap[key]
      }

      const seq = (detailFetchSeqMap.get(key) || 0) + 1
      detailFetchSeqMap.set(key, seq)

      commit('SET_DETAIL_LOADING', { id: key, loading: true })
      try {
        const data = await getPortalContentDetail(id, locale)
        if (detailFetchSeqMap.get(key) !== seq) return null

        const listItem = state.items.find((item) => String(item.id) === key) || null
        const detail = normalizeSolutionDetail(data, listItem)
        commit('SET_DETAIL', { id: key, detail })
        return detail
      } catch (error) {
        if (detailFetchSeqMap.get(key) !== seq) return null
        commit('SET_DETAIL', { id: key, detail: null })
        throw error
      } finally {
        if (detailFetchSeqMap.get(key) === seq) {
          commit('SET_DETAIL_LOADING', { id: key, loading: false })
        }
      }
    }
  }
}
